import { Canvas } from 'fabric';

import { MetaBulletSystem } from '../../metaBullets/managers/MetaBulletSystem';
import { TextEntity } from '../../metaBullets/classes/TextEntity';
import { Marker } from '../../metaBullets/classes/Marker';

import { relinkTextEntitiesWithMarkers } from './relinkTextEntitiesWithMarkers';

/**
 * Инициализировать систему метабуллитов после загрузки объектов с бэкенда
 * Находит все TextEntity на canvas, группирует их по metaBulletId и создает MetaBullet объекты
 */
export async function initializeMetaBulletsFromCanvas(
  canvas: Canvas,
  canvasId: string,
  options?: {
    skipBackupIfProgressive?: boolean;
  },
): Promise<MetaBulletSystem | null> {
  try {
    // ВАЖНО: Проверяем, не инициализирована ли уже система
    if (canvas._metaBulletSystem) {
      console.warn('⚠️ MetaBulletSystem already exists on canvas, destroying old one first');
      canvas._metaBulletSystem.destroy();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (canvas as any)._metaBulletSystem = undefined;
    }

    // Создаем и инициализируем систему
    const system = new MetaBulletSystem(canvas, canvasId);

    // ✅ Инициализируем BackupManager с функцией создания бекапов и проверки undo/redo
    const { useCanvasStore } = await import('../../store');
    const addBackup = useCanvasStore.getState().addBackup;
    const checkUndoRedoInProgress = () => useCanvasStore.getState().isUndoOrRedoInProgress;
    system.initializeBackupManager(addBackup, checkUndoRedoInProgress);

    system.initialize();

    // Начинаем процесс инициализации (отключаем бекапы)
    system.startInitialization();

    // Сохраняем ссылку на систему в canvas для доступа из MetaBullet
    canvas._metaBulletSystem = system;

    // 🎯 КРИТИЧНО: Пере-связываем TextEntity с Marker ЗДЕСЬ, а не раньше!
    // При загрузке объектов порядок добавления на canvas может быть непредсказуемым,
    // и relinkTextEntitiesWithMarkers может вызываться когда TextEntity еще не на canvas.
    relinkTextEntitiesWithMarkers(canvas);

    // Находим все TextEntity объекты на canvas
    const allObjects = canvas.getObjects();
    const textEntities = allObjects.filter((obj): obj is TextEntity =>
      TextEntity.isTextEntity(obj),
    );

    // console.debug(`📋 Found ${textEntities.length} TextEntity objects on canvas`);

    if (textEntities.length === 0) {
      // console.debug('✅ No TextEntity objects found, MetaBullet system ready');
      // Завершаем инициализацию (но бекап не нужен - нет объектов)
      system.finishInitialization();
      return system;
    }

    // Группируем TextEntity по metaBulletId
    const metaBulletGroups = new Map<string, TextEntity[]>();

    textEntities.forEach((entity) => {
      const metaBulletId = entity.metaBulletId;

      if (metaBulletId) {
        // Есть metaBulletId - добавляем в группу
        if (!metaBulletGroups.has(metaBulletId)) {
          metaBulletGroups.set(metaBulletId, []);
        }
        metaBulletGroups.get(metaBulletId)!.push(entity);
      } else {
        // Нет metaBulletId - создаем индивидуальный метабуллит
        // console.debug(
        //   '📝 Creating individual MetaBullet for entity without metaBulletId:',
        //   entity.id,
        // );
        system.createMetaBullet([entity]);
      }
    });

    // Создаем MetaBullet объекты для каждой группы
    for (const [metaBulletId, entities] of metaBulletGroups.entries()) {
      // console.debug(`🆕 Creating MetaBullet from backend data:`, {
      //   metaBulletId,
      //   entitiesCount: entities.length,
      //   entityIds: entities.map((e) => e.id),
      // });

      // Сортируем сущности по entityOrder перед созданием метабуллита
      const sortedEntities = entities.sort((a, b) => (a.entityOrder || 0) - (b.entityOrder || 0));

      // Создаем метабуллит с предопределенным ID из бэкенда
      const createdMetaBulletId = system.createMetaBullet(sortedEntities, metaBulletId);

      // Проверяем, что ID совпадает (для отладки)
      if (createdMetaBulletId !== metaBulletId) {
        console.warn('⚠️ MetaBullet ID mismatch:', {
          expected: metaBulletId,
          created: createdMetaBulletId,
        });
      }
    }

    // 🔢 УСТАНАВЛИВАЕМ СВЯЗИ DIGITAL-ACCENT
    // После создания всех объектов устанавливаем связи между digit и superscript
    textEntities.forEach((entity) => {
      if (entity.isDigitalAccent && entity.isDigitalAccent() && entity.linkedAccentEntityId) {
        const linkedEntity = textEntities.find((e) => e.id === entity.linkedAccentEntityId);
        if (linkedEntity && linkedEntity.isDigitalAccent && linkedEntity.isDigitalAccent()) {
          entity.setLinkedAccentEntity(linkedEntity);
        } else {
          console.warn('🔢 Digital-accent linked entity not found:', {
            entityId: entity.id,
            linkedAccentEntityId: entity.linkedAccentEntityId,
          });
        }
      }
    });

    // 🎯 ИСПРАВЛЕНИЕ: СНАЧАЛА обновляем позиции всех маркеров согласно textEntityStyles
    // Это КРИТИЧЕСКИ важно делать ДО recalculatePositions, потому что recalculatePositions
    // использует getLinkedMarkers() для расчета границ MetaBullet
    textEntities.forEach((entity) => {
      if (entity.updateMarkerPosition) {
        entity.updateMarkerPosition();
      }
    });

    // 🎯 РЕШЕНИЕ ПРОБЛЕМЫ 1: Сохраняем состояние ДО рекалькуляции для сравнения
    const stateBeforeRecalculation: Record<string, { left: number; top: number }> = {};
    textEntities.forEach((entity) => {
      stateBeforeRecalculation[entity.id] = {
        left: entity.left || 0,
        top: entity.top || 0,
      };
    });

    // Теперь пересчитываем позиции для всех метабуллитов С ПРАВИЛЬНЫМИ координатами маркеров
    const allMetaBullets = system.getAllMetaBullets();

    // Пересчитываем позиции для каждого метабуллита
    const recalculationPromises = allMetaBullets.map((metaBullet) => {
      return metaBullet.recalculatePositions(false); // Без анимации при инициализации
    });

    // Ждем завершения всех пересчетов
    await Promise.all(recalculationPromises);

    // 🔄 СИНХРОНИЗАЦИЯ: После рекалькуляции синхронизируем контент-объекты маркеров
    // Это нужно потому что бэкенд передает абсолютные координаты, а после рекалькуляции
    // коробка маркера может встать в другую позицию
    const allCanvasObjects = canvas.getObjects();
    const markers = allCanvasObjects.filter(
      (obj): obj is Marker => obj.type === 'marker' && !!(obj as Marker).isMarker,
    ) as Marker[];

    markers.forEach((marker) => {
      if (marker.syncContentObjectPositionSync) {
        marker.syncContentObjectPositionSync();
      }
    });

    // РЕШЕНИЕ ПРОБЛЕМЫ 1: Сравниваем состояния ДО и ПОСЛЕ рекалькуляции
    let hasRealChanges = false;
    const positionChanges: Array<{
      entityId: string;
      before: { left: number; top: number };
      after: { left: number; top: number };
      deltaX: number;
      deltaY: number;
    }> = [];

    textEntities.forEach((entity) => {
      const before = stateBeforeRecalculation[entity.id];
      const after = { left: entity.left || 0, top: entity.top || 0 };

      // Сравниваем с допуском 1px (для компенсации погрешностей округления)
      const deltaX = Math.abs(after.left - before.left);
      const deltaY = Math.abs(after.top - before.top);

      if (deltaX > 1 || deltaY > 1) {
        hasRealChanges = true;
        positionChanges.push({
          entityId: entity.id,
          before,
          after,
          deltaX,
          deltaY,
        });
      }
    });

    if (hasRealChanges) {
      console.debug('📊 Position changes detected after recalculation:', {
        totalChanges: positionChanges.length,
        changes: positionChanges.slice(0, 5), // Показываем первые 5 изменений
      });
      system.markPositionChanges(); // Отмечаем что были изменения - нужен бекап
    } else {
      console.debug('✅ No position changes after recalculation - backup not needed');
      // 🎯 КРИТИЧНО: Сбрасываем флаг если позиции не изменились!
      // Флаг мог быть установлен во время recalculatePositions, но если итоговые позиции
      // совпадают с начальными - бэкап не нужен
      system.hasPositionChanges = false;
    }

    // 🔢 ОБНОВЛЯЕМ ПОЗИЦИИ DIGITAL-ACCENT ПОСЛЕ РЕКАЛЬКУЛЯЦИИ
    // После рекалькуляции позиций метабуллитов обновляем позиции superscript
    textEntities.forEach((entity) => {
      if (entity.isDigitalAccentDigit && entity.isDigitalAccentDigit()) {
        entity.updateLinkedAccentPosition();
      }
    });

    // Завершаем инициализацию и создаем бекап если были изменения
    // ⚠️ ВАЖНО: Не создаем бэкап если прогрессивная загрузка еще идет!
    system.finishInitialization({
      skipBackup: options?.skipBackupIfProgressive || false,
    });

    // Выводим статистику
    // const systemInfo = system.getDebugInfo();
    // console.debug('✅ MetaBullet system initialized:', systemInfo);

    return system;
  } catch (error) {
    console.error('❌ Error initializing MetaBullet system:', error);
    return null;
  }
}

/**
 * Очистить все метабуллиты на canvas (для пересоздания)
 */
export function clearMetaBulletsFromCanvas(canvas: Canvas): void {
  // console.debug('🧹 Clearing all MetaBullet data from canvas objects...');

  const allObjects = canvas.getObjects();
  const textEntities = allObjects.filter((obj): obj is TextEntity => TextEntity.isTextEntity(obj));

  // Очищаем metaBulletId у всех TextEntity
  textEntities.forEach((entity) => {
    entity.metaBulletId = '';
    entity.entityOrder = 0;
  });

  // console.debug(`✅ Cleared MetaBullet data from ${textEntities.length} TextEntity objects`);
}
